import Vue from 'vue'
import Router from 'vue-router'

import store from '@/store'
import { loadLocale } from '@/locale/i18n'

Vue.use(Router)

const authRequired = (to, from, next) => {
  if (store.getters['AuthModule/isAuthenticated']) {
    next()
    return
  }

  next({
    name: 'auth_login'
  })
}
const onlyGuest = (to, from, next) => {
  if (store.getters['AuthModule/isAuthenticated']) {
    next(false)
  }
  next()
}

export default new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior (to, from, savedPosition) {
    if (to.hash) {
      return { selector: to.hash }
    }
    return { x: 0, y: 0 }
  },
  beforeEach (to, from, next) {
    const lang = to.params.lang
    loadLocale(lang).then(() => next())
  },
  routes: [
    // {
    //   path: '/test',
    //   name: 'test',
    //   component: () => import(/* webpackChunkName: 'test' */ '@/pages/Test')
    // },
    {
      path: '/',
      afterEnter: (to, from, next) => {
        next({ name: 'home' })
      },
      component: () => import(/* webpackChunkName: "index" */ '@/layouts/Index'),
      children: [
        {
          path: '',
          name: 'home',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/Home')
        },
        {
          path: 'parishes',
          name: 'list_of_parishes',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/Parishes')
        },
        {
          path: 'parish/:parish_id',
          name: 'show_parish',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/ParishPage')
        },
        {
          path: 'parish/:parish_id/blog',
          name: 'blog',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/Blog')
        },
        {
          path: 'parish/:parish_id/blog/:post_id',
          name: 'blog_post',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/Blog')
        },
        {
          path: 'priests',
          name: 'list_of_priests',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/Priests')
        },
        {
          path: 'sermons',
          name: 'coming_sermons',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/Sermons')
        },
        {
          path: 'archive',
          name: 'sermons_archive',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/Archive')
        },
        {
          path: 'mission',
          name: 'our_mission',
          component: () => import(/* webpackChunkName: "index" */ '@/pages/Mission')
        },
        {
          path: 'news/:alias',
          name: 'news_item',
          component: () => import(/* webpackChunkName: 'index' */ '@/pages/NewsItem')
        },
        {
          path: 'news',
          name: 'news',
          component: () => import(/* webpackChunkName: 'index' */ '@/pages/News')
        },
        {
          path: '/policy',
          name: 'policy',
          component: () => import(/* webpackChunkName: 'policy' */ '@/pages/Policy')
        },
        {
          path: '/finance-information',
          name: 'finance_information',
          component: () => import(/* webpackChunkName: 'policy' */ '@/pages/FinanceInformation')
        }
      ]
    },
    {
      path: '/',
      component: () => import(/* webpackChunkName: "auth" */ '@/layouts/Auth'),
      children: [
        {
          path: 'login/:code',
          name: 'auth_auto_login',
          component: () => import(/* webpackChunkName: "auth" */ '@/pages/auth/AutoLogin')
        },
        {
          path: 'login',
          name: 'auth_login',
          beforeEnter: onlyGuest,
          component: () => import(/* webpackChunkName: "auth" */ '@/pages/auth/Login')
        },
        {
          path: 'register',
          name: 'auth_register',
          beforeEnter: onlyGuest,
          component: () => import(/* webpackChunkName: "auth" */ '@/pages/auth/Register')
        },
        {
          path: 'register-priest',
          name: 'auth_register_register',
          beforeEnter: onlyGuest,
          component: () => import(/* webpackChunkName: "auth" */ '@/pages/auth/PriestRegister')
        },
        {
          path: 'reset',
          name: 'auth_reset',
          beforeEnter: onlyGuest,
          component: () => import(/* webpackChunkName: "auth" */ '@/pages/auth/Reset')
        },
        {
          path: 'logout',
          name: 'auth_logout',
          beforeEnter: async (to, from, next) => {
            await store.dispatch('AuthModule/logout', {}, { root: true })
            next({ name: 'home' })
          }
        },
        {
          path: 'confirm/:code',
          name: 'auth_confirm',
          component: () => import(/* webpackChunkName: "auth" */ '@/pages/auth/Confirm')
        }
      ]
    },
    {
      path: '/',
      beforeEnter: authRequired,
      component: () => import(/* webpackChunkName: "cabinet" */ '@/layouts/Cabinet'),
      children: [
        {
          path: 'dashboard',
          beforeEnter: authRequired,
          name: 'cabinet',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/Cabinet')
        },
        {
          path: 'welcome',
          beforeEnter: authRequired,
          name: 'cabinet_welcome',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/Welcome')
        },
        {
          path: 'profile',
          beforeEnter: authRequired,
          name: 'cabinet_profile',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/Profile')
        },
        {
          path: 'chat',
          beforeEnter: authRequired,
          name: 'cabinet_chat',
          component: () => import(/* webpackChunkName: "cabinet_chat" */ '@/pages/cabinet/Chat')
        },
        {
          path: 'chat/:room_id',
          beforeEnter: authRequired,
          name: 'cabinet_chat_room',
          component: () => import(/* webpackChunkName: "cabinet_chat" */ '@/pages/cabinet/ChatRoom')
        },
        {
          path: 'conference',
          beforeEnter: authRequired,
          name: 'cabinet_conference',
          component: () => import(/* webpackChunkName: "cabinet_conference" */ '@/pages/cabinet/Conference')
        },
        {
          path: 'blog',
          beforeEnter: authRequired,
          name: 'cabinet_blog',
          component: () => import(/* webpackChunkName: "cabinet_blog" */ '@/pages/cabinet/Blog')
        },
        {
          path: 'shrift',
          beforeEnter: authRequired,
          name: 'cabinet_shrift',
          component: () => import(/* webpackChunkName: "cabinet_shrift" */ '@/pages/cabinet/Shrift')
        },
        {
          path: 'shrift-test',
          beforeEnter: authRequired,
          name: 'cabinet_shrift_test',
          component: () => import(/* webpackChunkName: "cabinet_shrift_test" */ '@/pages/cabinet/ShriftTest')
        },
        {
          path: 'parishioners',
          beforeEnter: authRequired,
          name: 'cabinet_parishioners',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/Parishioners')
        },
        {
          path: 'parish',
          beforeEnter: authRequired,
          name: 'cabinet_parish',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/Parish')
        },
        {
          path: 'donations',
          beforeEnter: authRequired,
          name: 'cabinet_donations',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/Donations')
        },
        {
          path: 'donations/cashbox',
          beforeEach: authRequired,
          name: 'cabinet_donations_cashbox',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/CashBox')
        },
        {
          path: 'invites',
          beforeEach: authRequired,
          name: 'cabinet_invites',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/Invites')
        },
        {
          path: 'messages',
          beforeEach: authRequired,
          name: 'cabinet_messages',
          component: () => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinet/Messages')
        }
      ]
    },
    {
      path: '*',
      component: () => import(/* webpackChunkName: "errors" */ '@/layouts/Errors'),
      children: [
        {
          path: '',
          name: 'error_404',
          component: () => import(/* webpackChunkName: "errors" */ '@/pages/errors/404')
        }
      ]
    }
  ]
})
